import {
  NotificationDetailsDto,
  NotificationDto,
  NotificationListResponseDto,
  NotificationsListResponseDto,
  SendEmailValidationResponseDto,
  SetMailFromResponseDto,
  NotificationSettingsDto,
  UpdateNotificationRequestDto,
  VerifyInmailRecordResponseDto,
  VerifyValidationEmailResponseDto,
  WhatsappCreditsPacksResponseDto,
  WhatsappPurchaseRequestDto,
  WhatsappPurchaseResponseDto,
  WhatsappPurchaseSyncResponseDto,
  WhatsappPurchasesListResponseDto,
  WhatsappCreditsBalanceResponseDto,
  WhatsappTemplateResponseDto,
} from '@tiendanube/common';
import { cirrusApi } from 'App/rtk';
import getUrlWithParams from 'commons/utils/getUrlWithParams';
import {
  BASE_NOTIFICATIONS_URL,
  EMAIL_BY_ID,
  SEND_EMAIL_VALIDATION,
  SET_MAIL_FROM,
  VERIFY_INMAIL_RECORD,
  VERIFY_VALIDATION_EMAIL,
  WHATSAPP_SETTINGS_URL,
  WHATSAPP_NOTIFICATIONS_URL,
  WHATSAPP_NOTIFICATION_BY_ID_URL,
  WHATSAPP_CREDITS_PACKS_URL,
  WHATSAPP_CREDITS_BALANCE_URL,
  WHATSAPP_PURCHASES_URL,
  WHATSAPP_PURCHASE_SYNC_URL,
  WHATSAPP_TEMPLATE_BY_ID_URL,
  emailEndpoints,
  emailTags,
  whatsappEndpoints,
  whatsappTags,
} from './config';

export const notificationsApi = cirrusApi
  .enhanceEndpoints({
    addTagTypes: [
      emailTags.emailsList,
      whatsappTags.notificationSettings,
      whatsappTags.notification,
      whatsappTags.notifications,
      whatsappTags.creditsPacks,
      whatsappTags.creditsBalance,
      whatsappTags.purchases,
      whatsappTags.template,
    ],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      [emailEndpoints.getEmailsList]: builder.query<
        NotificationsListResponseDto,
        void
      >({
        query: () => ({
          method: 'GET',
          url: BASE_NOTIFICATIONS_URL,
        }),
        providesTags: [emailTags.emailsList],
      }),
      [emailEndpoints.getEmail]: builder.query<NotificationDetailsDto, string>({
        query: (id) => ({
          method: 'GET',
          url: getUrlWithParams(EMAIL_BY_ID, { id }),
        }),
      }),
      [emailEndpoints.updateEmail]: builder.mutation<
        NotificationDetailsDto,
        {
          id: string;
          payload: {
            data: NotificationDetailsDto;
            initialValues: NotificationDetailsDto;
          };
        }
      >({
        query: ({ id, payload }) => ({
          method: 'PUT',
          url: getUrlWithParams(EMAIL_BY_ID, { id }),
          data: payload,
        }),
      }),
      [emailEndpoints.partialUpdateEmail]: builder.mutation<
        NotificationDetailsDto,
        {
          id: string;
          payload: {
            data: Partial<NotificationDetailsDto>;
          };
        }
      >({
        query: ({ id, payload }) => ({
          method: 'PATCH',
          url: getUrlWithParams(EMAIL_BY_ID, { id }),
          data: payload,
        }),
      }),
      [emailEndpoints.sendEmailValidation]: builder.mutation<
        SendEmailValidationResponseDto,
        void
      >({
        query: () => ({
          method: 'POST',
          url: SEND_EMAIL_VALIDATION,
        }),
      }),
      [emailEndpoints.verifyValidationEmail]: builder.mutation<
        VerifyValidationEmailResponseDto,
        void
      >({
        query: () => ({
          method: 'POST',
          url: VERIFY_VALIDATION_EMAIL,
        }),
      }),
      [emailEndpoints.verifyInmailRecord]: builder.query<
        VerifyInmailRecordResponseDto,
        void
      >({
        query: () => ({
          method: 'GET',
          url: VERIFY_INMAIL_RECORD,
        }),
      }),
      [emailEndpoints.setMailFrom]: builder.mutation<
        SetMailFromResponseDto,
        void
      >({
        query: () => ({
          method: 'POST',
          url: SET_MAIL_FROM,
        }),
      }),
      [whatsappEndpoints.getNotificationList]: builder.query<
        NotificationListResponseDto,
        void
      >({
        query: () => ({
          method: 'GET',
          url: WHATSAPP_NOTIFICATIONS_URL,
        }),
        providesTags: [whatsappTags.notifications],
      }),
      [whatsappEndpoints.getNotificationById]: builder.query<
        NotificationDto,
        string
      >({
        query: (id) => ({
          method: 'GET',
          url: getUrlWithParams(WHATSAPP_NOTIFICATION_BY_ID_URL, { id }),
        }),
        providesTags: (_result, _error, id) => [
          { type: whatsappTags.notification, id },
        ],
      }),
      [whatsappEndpoints.updateNotificationById]: builder.mutation<
        NotificationDto,
        {
          id: string;
          payload: UpdateNotificationRequestDto;
        }
      >({
        query: ({ id, payload }) => ({
          method: 'PUT',
          url: getUrlWithParams(WHATSAPP_NOTIFICATION_BY_ID_URL, { id }),
          data: payload,
        }),
        invalidatesTags: (_result, _error, { id }) => [
          whatsappTags.notifications,
          { type: whatsappTags.notification, id },
          whatsappTags.notificationSettings,
        ],
      }),
      [whatsappEndpoints.getNotificationSettings]: builder.query<
        NotificationSettingsDto,
        void
      >({
        query: () => ({
          method: 'GET',
          url: WHATSAPP_SETTINGS_URL,
        }),
        providesTags: [whatsappTags.notificationSettings],
      }),
      [whatsappEndpoints.getCreditsPacks]: builder.query<
        WhatsappCreditsPacksResponseDto,
        void
      >({
        query: () => ({
          method: 'GET',
          url: WHATSAPP_CREDITS_PACKS_URL,
        }),
        providesTags: [whatsappTags.creditsPacks],
      }),
      [whatsappEndpoints.getCreditsBalance]: builder.query<
        WhatsappCreditsBalanceResponseDto,
        void
      >({
        query: () => ({
          method: 'GET',
          url: WHATSAPP_CREDITS_BALANCE_URL,
        }),
        providesTags: [whatsappTags.creditsBalance],
      }),
      [whatsappEndpoints.getPurchases]: builder.query<
        WhatsappPurchasesListResponseDto,
        void
      >({
        query: () => ({
          method: 'GET',
          url: WHATSAPP_PURCHASES_URL,
        }),
        providesTags: [whatsappTags.purchases],
      }),
      [whatsappEndpoints.createPurchase]: builder.mutation<
        WhatsappPurchaseResponseDto,
        WhatsappPurchaseRequestDto
      >({
        query: (payload) => ({
          method: 'POST',
          url: WHATSAPP_PURCHASES_URL,
          data: payload,
        }),
        invalidatesTags: [whatsappTags.purchases, whatsappTags.creditsBalance],
      }),
      [whatsappEndpoints.syncPurchase]: builder.mutation<
        WhatsappPurchaseSyncResponseDto,
        string
      >({
        query: (id) => ({
          method: 'POST',
          url: getUrlWithParams(WHATSAPP_PURCHASE_SYNC_URL, { id }),
        }),
        invalidatesTags: [whatsappTags.purchases, whatsappTags.creditsBalance],
      }),
      [whatsappEndpoints.getTemplateById]: builder.query<
        WhatsappTemplateResponseDto,
        string
      >({
        query: (id) => ({
          method: 'GET',
          url: getUrlWithParams(WHATSAPP_TEMPLATE_BY_ID_URL, { id }),
        }),
        providesTags: (_result, _error, id) => [
          { type: whatsappTags.template, id },
        ],
      }),
    }),
  });

export const {
  useGetEmailsListQuery,
  useGetEmailQuery,
  useUpdateEmailMutation,
  usePartialUpdateEmailMutation,
  useSendEmailValidationMutation,
  useVerifyValidationEmailMutation,
  useLazyVerifyInmailRecordQuery,
  useSetMailFromMutation,
} = notificationsApi;

export const {
  useGetWhatsappNotificationListQuery,
  useGetWhatsappNotificationByIdQuery,
  useUpdateWhatsappNotificationByIdMutation,
  useGetWhatsappNotificationSettingsQuery,
  useGetWhatsappCreditsPacksQuery,
  useGetWhatsappCreditsBalanceQuery,
  useGetWhatsappPurchasesQuery,
  useCreateWhatsappPurchaseMutation,
  useSyncWhatsappPurchaseMutation,
  useGetWhatsappTemplateByIdQuery,
} = notificationsApi;
